import Link from "next/link";
import { GoArrowUpRight } from "react-icons/go";
import { PiMapPinLight } from "react-icons/pi";

export type JobBadgeTone = "green" | "blue" | "orange";

export interface JobListing {
    id: string;
    /** Career detail route segment */
    slug: string;
    title: string;
    department: string;
    description: string;
    location: string;
    salaryRange: string;
    /** Map / external URL for the location row (optional). */
    href: string;
    /** When set, overrides the tone derived from `id`. */
    badgeTone?: JobBadgeTone;
}

const BADGE_TONES: JobBadgeTone[] = ["green", "blue", "orange"];

/** Stable pseudo-random tone per id (avoids SSR/client mismatch vs Math.random). */
const badgeToneFromId = (id: string): JobBadgeTone => {
    let n = 0;
    for (let i = 0; i < id.length; i++) {
        n = (n + id.charCodeAt(i) * (i + 1)) >>> 0;
    }
    const u = ((n ^ 0x9e3779b9) >>> 0) % BADGE_TONES.length;
    return BADGE_TONES[u]!;
};

const BADGE_STYLES: Record<JobBadgeTone, string> = {
    green:
        "rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-100/80",
    blue: "rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-sky-100/80",
    orange:
        "rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-900 ring-1 ring-orange-100/80",
};

const isExternalHref = (url: string) =>
    url.startsWith("http://") || url.startsWith("https://");

interface CareerJobCardProps {
    job: JobListing;
}

const CareerJobCard = ({ job }: CareerJobCardProps) => {
    const tone: JobBadgeTone = job.badgeTone ?? badgeToneFromId(job.id);
    const locationHref = job.href?.trim();
    const locationHasLink = Boolean(locationHref && locationHref !== "#");
    const locationExternal = locationHasLink && isExternalHref(locationHref!);
    const detailHref = job.slug ? `/career/${job.slug}` : "#";

    return (
        <article className="relative flex flex-col gap-6 py-4 md:py-8 md:flex-row md:items-center md:justify-between md:gap-20 px-1 border-b-1 border-silverBlue">
            <Link
                href={detailHref}
                className="absolute inset-0 z-0 rounded-sm outline-offset-2 focus-visible:z-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-silverBlue"
                aria-label={`View job: ${job.title}`}
            />
            <div className="relative z-10 flex w-full flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-20 pointer-events-none">
                <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-baseline gap-4">
                        <h3 className="font-heading text-lg font-semibold text-textf1 md:text-xl">{job.title}</h3>
                        <span className={BADGE_STYLES[tone]}>{job.department}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-textf2 md:text-base/[25.6px] line-clamp-2">{job.description}</p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                        {locationHasLink ? (
                            <a
                                href={locationHref!}
                                className="pointer-events-auto inline-flex items-center gap-1.5 text-textf2 transition-opacity hover:opacity-80"
                                {...(locationExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            >
                                <PiMapPinLight className="shrink-0 text-base text-darkBrown" aria-hidden />
                                {job.location}
                            </a>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-textf2">
                                <PiMapPinLight className="shrink-0 text-base text-darkBrown" aria-hidden />
                                {job.location}
                            </span>
                        )}
                        <span className="font-medium text-textf2">{job.salaryRange}</span>
                    </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 self-start font-medium text-textf1 md:self-center">
                    <span>View Job</span>
                    <GoArrowUpRight className="text-lg" aria-hidden />
                </span>
            </div>
        </article>
    );
};

export default CareerJobCard;
